import { useState, useEffect } from 'react';
import { Download, Smartphone, X, Apple, Share, Plus, MoreVertical, Monitor, Check } from 'lucide-react';

/**
 * Always-visible "Install Tovías" button + modal with per-device instructions.
 *
 * Behaviour:
 *  - On Chrome/Edge Android, captures the BeforeInstallPromptEvent and offers
 *    a one-tap install button.
 *  - On iOS Safari & desktop Safari, shows the manual "share → add to home screen"
 *    instructions because Safari does not expose the install API.
 *  - If the app is already installed (standalone display mode), shows a confirmation.
 */
const InstallAppButton = ({ className = '', variant = 'primary' }) => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [device, setDevice] = useState('other');

  // Detect device family on mount (rough, but enough to pick the right tab).
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isStandalone =
      window.matchMedia && window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    setDevice(isIOS ? 'ios' : isAndroid ? 'android' : 'desktop');
    setInstalled(isStandalone || window.__pwaInstalled === true);

    // Pick up an event that was captured by the head-script BEFORE React mounted.
    if (window.__deferredInstallPrompt) {
      setDeferredPrompt(window.__deferredInstallPrompt);
    }

    const onBefore = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onAvailable = () => { if (window.__deferredInstallPrompt) setDeferredPrompt(window.__deferredInstallPrompt); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('pwa-install-available', onAvailable);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('pwa-install-available', onAvailable);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const tryNativeInstall = async () => {
    const prompt = deferredPrompt || window.__deferredInstallPrompt;
    if (!prompt) return;
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') { setInstalled(true); setOpen(false); }
      setDeferredPrompt(null);
      window.__deferredInstallPrompt = null;
    } catch (e) {
      // fall through to manual instructions
    }
  };

  const baseBtn = variant === 'compact'
    ? 'inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-200 hover:bg-stone-50 text-stone-800 text-sm font-semibold shadow-sm transition-all'
    : 'inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all';

  return (
    <>
      <button
        type="button"
        onClick={async () => {
          // 1-tap install when the browser supports it AND has fired the
          // beforeinstallprompt event (Chrome/Edge on Android & desktop).
          const prompt = deferredPrompt || window.__deferredInstallPrompt;
          if (prompt && !installed) {
            try {
              prompt.prompt();
              const { outcome } = await prompt.userChoice;
              if (outcome === 'accepted') {
                setInstalled(true);
              }
              setDeferredPrompt(null);
              window.__deferredInstallPrompt = null;
              return;
            } catch (_e) {
              // Fall through to the manual instructions modal.
            }
          }
          // Fallback: iOS Safari, Firefox, or Chrome that already swallowed
          // the prompt — show the per-device step-by-step instructions.
          setOpen(true);
        }}
        data-testid="install-app-btn"
        className={`${baseBtn} ${className}`}
      >
        {installed ? <Check className="w-4 h-4" /> : <Download className="w-4 h-4" />}
        <span>{installed ? 'Tovías instalada' : 'Instalar Tovías'}</span>
      </button>

      {open && (
        <InstallModal
          device={device}
          installed={installed}
          deferredPrompt={deferredPrompt}
          onClose={() => setOpen(false)}
          onNativeInstall={tryNativeInstall}
          onDeviceChange={setDevice}
        />
      )}
    </>
  );
};

/* ------------------------------ Modal -------------------------------- */

const InstallModal = ({ device, installed, deferredPrompt, onClose, onNativeInstall, onDeviceChange }) => {
  const tabs = [
    { id: 'android', label: 'Android', icon: Smartphone },
    { id: 'ios', label: 'iPhone / iPad', icon: Apple },
    { id: 'desktop', label: 'Escritorio', icon: Monitor },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      data-testid="install-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-br from-teal-600 to-teal-700 text-white flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold opacity-80">Instalación</p>
            <h2 className="text-xl sm:text-2xl font-bold mt-0.5" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              📲 Instala Tovías en tu móvil
            </h2>
            <p className="text-sm text-teal-50/90 mt-1">
              {installed ? 'Tovías ya está instalada en este dispositivo.' : 'Funciona como app nativa, sin pasar por las tiendas.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 -mt-1 -mr-1"
            data-testid="install-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => onDeviceChange(id)}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors ${
                device === id ? 'border-teal-600 text-teal-700 bg-teal-50/50' : 'border-transparent text-stone-500 hover:text-stone-800'
              }`}
              data-testid={`install-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {device === 'android' && (
            <AndroidInstructions deferredPrompt={deferredPrompt} onNativeInstall={onNativeInstall} />
          )}
          {device === 'ios' && <IOSInstructions />}
          {device === 'desktop' && <DesktopInstructions deferredPrompt={deferredPrompt} onNativeInstall={onNativeInstall} />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-100 text-center">
          <p className="text-xs text-stone-500">
            Tovías es una <strong>PWA</strong> (Progressive Web App). Se instala desde el navegador, no ocupa apenas espacio y se actualiza sola.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ----------------------- Per-device instructions --------------------- */

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start">
    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 inline-flex items-center justify-center text-sm font-bold">
      {n}
    </span>
    <div className="pt-0.5 text-sm text-stone-700 leading-relaxed">{children}</div>
  </li>
);

const AndroidInstructions = ({ deferredPrompt, onNativeInstall }) => (
  <div className="space-y-4">
    {deferredPrompt ? (
      <div className="rounded-xl bg-teal-50 border border-teal-200 p-4 mb-2">
        <p className="text-sm text-teal-900 mb-3">
          Tu navegador soporta la instalación en un toque. Pulsa el botón:
        </p>
        <button
          type="button"
          onClick={onNativeInstall}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm shadow-md"
          data-testid="install-modal-native-android"
        >
          <Download className="w-4 h-4" /> Instalar ahora
        </button>
      </div>
    ) : null}
    <p className="text-sm font-semibold text-stone-900">O hazlo manualmente desde Chrome:</p>
    <ol className="space-y-3">
      <Step n="1">Abre <strong>tovias.app</strong> en Chrome.</Step>
      <Step n="2">Pulsa el menú <MoreVertical className="inline w-4 h-4 align-text-bottom" /> (los 3 puntos arriba a la derecha).</Step>
      <Step n="3">Selecciona <strong>«Instalar app»</strong> o <strong>«Añadir a pantalla de inicio»</strong>.</Step>
      <Step n="4">Confirma y ¡listo! Tovías aparecerá entre tus apps.</Step>
    </ol>
  </div>
);

const IOSInstructions = () => (
  <div className="space-y-4">
    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
      <strong>Importante:</strong> En iOS solo funciona desde <strong>Safari</strong> (no desde Chrome ni Firefox).
    </div>
    <ol className="space-y-3">
      <Step n="1">Abre <strong>tovias.app</strong> en <strong>Safari</strong>.</Step>
      <Step n="2">
        Pulsa el icono <Share className="inline w-4 h-4 align-text-bottom" /> <strong>«Compartir»</strong> en la barra inferior.
      </Step>
      <Step n="3">
        Desliza hacia abajo en el menú y selecciona <Plus className="inline w-4 h-4 align-text-bottom" /> <strong>«Añadir a pantalla de inicio»</strong>.
      </Step>
      <Step n="4">Confirma con «Añadir». El icono de Tovías aparecerá en tu pantalla de inicio.</Step>
    </ol>
  </div>
);

const DesktopInstructions = ({ deferredPrompt, onNativeInstall }) => (
  <div className="space-y-4">
    {deferredPrompt ? (
      <div className="rounded-xl bg-teal-50 border border-teal-200 p-4 mb-2">
        <p className="text-sm text-teal-900 mb-3">
          Puedes instalarla como app de escritorio en un clic:
        </p>
        <button
          type="button"
          onClick={onNativeInstall}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm shadow-md"
          data-testid="install-modal-native-desktop"
        >
          <Download className="w-4 h-4" /> Instalar ahora
        </button>
      </div>
    ) : null}
    <p className="text-sm font-semibold text-stone-900">Chrome / Edge / Brave:</p>
    <ol className="space-y-3">
      <Step n="1">Busca el icono <Download className="inline w-4 h-4 align-text-bottom" /> en la barra de direcciones (a la derecha de la URL).</Step>
      <Step n="2">Haz clic en él y selecciona <strong>«Instalar»</strong>.</Step>
      <Step n="3">Tovías se abrirá como una ventana independiente y aparecerá en tu menú de apps.</Step>
    </ol>
    <p className="text-sm font-semibold text-stone-900 mt-4">Safari (macOS):</p>
    <ol className="space-y-3">
      <Step n="1">Menú <strong>Archivo</strong> → <strong>«Añadir al Dock»</strong>.</Step>
    </ol>
  </div>
);

export default InstallAppButton;
